import { z } from 'zod';

import { isRtClassKey, type RtClassKey } from '../fiscal/rt-class';

/**
 * Reparti fiscali (RT) — modello condiviso FE/BE.
 *
 *   reparto = nome libero + aliquota/natura dal catalogo (solo righe
 *             proiettabili su classe RT) + categorie prodotto collegate
 *             (zero o più).
 *
 *   - `code` è il NUMERO di reparto programmato sul registratore (slot
 *     fisico): intero univoco per tenant, usato tale quale nello scontrino e
 *     nella verifica configurazione (niente più "posizione nel mapping").
 *   - più reparti della stessa classe RT sono legittimi ("Telefonia 22%",
 *     "Accessori 22%"); le categorie servono SOLO a distinguerli alla vendita
 *     (classe della riga + categoria del prodotto → reparto); una categoria
 *     può servire un solo reparto per classe (vincolo a DB).
 *   - la configurazione per stampante vive in `pos_fiscal_printer_departments`
 *     (reparti attivati + un default per classe per stampante).
 *   - nessun obbligo di copertura: solo suggerimenti informativi.
 */

const UUID = z.string().uuid();
const wireTimestamp = z.union([z.string(), z.date()]);
const wireNumeric = z.union([z.string(), z.number()]);

/** Codice reparto = slot del registratore: intero 1..999. */
export const FISCAL_DEPARTMENT_CODE_MIN = 1;
export const FISCAL_DEPARTMENT_CODE_MAX = 999;
export const FiscalDepartmentCodeSchema = z.number().int()
  .min(FISCAL_DEPARTMENT_CODE_MIN, `Il codice reparto deve essere un intero tra ${FISCAL_DEPARTMENT_CODE_MIN} e ${FISCAL_DEPARTMENT_CODE_MAX}`)
  .max(FISCAL_DEPARTMENT_CODE_MAX, `Il codice reparto deve essere un intero tra ${FISCAL_DEPARTMENT_CODE_MIN} e ${FISCAL_DEPARTMENT_CODE_MAX}`);

export const RtClassKeySchema = z.string()
  .refine(isRtClassKey, 'Classe RT non valida (attesa aliquota es. "22" o radice natura N1..N6)')
  .transform((v): RtClassKey => v);

// ---------------------------------------------------------------------------
// Categorie collegate al reparto
// ---------------------------------------------------------------------------

/** Categoria collegata a un reparto (riga di `pos_fiscal_department_categories` arricchita). */
export const posFiscalDepartmentCategorySchema = z.object({
  category_id: z.string(),
  category_name: z.string(),
  parent_category_id: z.string().nullable(),
  parent_category_name: z.string().nullable(),
});
export type PosFiscalDepartmentCategory = z.infer<typeof posFiscalDepartmentCategorySchema>;

// ---------------------------------------------------------------------------
// GET /api/pos/fiscal-departments — riga reparto (JOIN aliquota + categorie)
// ---------------------------------------------------------------------------
export const posFiscalDepartmentRowSchema = z.object({
  id: z.string(),
  tenant_id: z.string(),
  code: z.number().int(),
  name: z.string(),
  color: z.string().nullable(),
  sort_order: z.number().nullable(),
  is_active: z.boolean(),
  created_at: wireTimestamp,
  updated_at: wireTimestamp,
  vat_rate_id: z.string(),
  // Alias da LEFT JOIN pos_vat_rates → nullable (nessuna riga corrispondente):
  vat_rate: wireNumeric.nullable(),
  vat_code: z.string().nullable(),
  vat_label: z.string().nullable(),
  vat_nature_code: z.string().nullable(),
  /** Classe RT derivata dall'aliquota (null solo se l'aliquota è sparita). */
  rt_class: z.string().nullable(),
  categories: z.array(posFiscalDepartmentCategorySchema),
});
export type PosFiscalDepartmentRow = z.infer<typeof posFiscalDepartmentRowSchema>;

/** Body POST/PUT /api/pos/fiscal-departments. In PUT ogni campo è opzionale;
 *  `category_ids` presente = sostituzione integrale dei collegamenti. */
export const posFiscalDepartmentInputSchema = z.object({
  code: FiscalDepartmentCodeSchema.optional(),
  name: z.string().trim().min(1).max(100).optional(),
  vat_rate_id: UUID.optional(),
  color: z.string().max(20).nullable().optional(),
  sort_order: z.number().int().optional(),
  is_active: z.boolean().optional(),
  category_ids: z.array(UUID).max(500).optional(),
}).strict();
export type PosFiscalDepartmentInput = z.infer<typeof posFiscalDepartmentInputSchema>;

// ---------------------------------------------------------------------------
// GET /api/pos/fiscal-departments/category-hints — per il form reparto:
// classi IVA realmente usate dai prodotti attivi di ogni categoria + reparti
// che già la collegano (per classe).
// ---------------------------------------------------------------------------
export interface FiscalDepartmentCategoryClassUsage {
  rt_class: RtClassKey;
  label: string;
  product_count: number;
}
export interface FiscalDepartmentCategoryLinkedDepartment {
  fiscal_department_id: string;
  code: number;
  name: string;
  rt_class: RtClassKey;
}
export interface FiscalDepartmentCategoryHint {
  category_id: string;
  name: string;
  parent_id: string | null;
  parent_name: string | null;
  is_active: boolean;
  /** Classi RT dei prodotti ATTIVI della categoria (vuoto = nessun prodotto). */
  classes: FiscalDepartmentCategoryClassUsage[];
  linked_departments: FiscalDepartmentCategoryLinkedDepartment[];
}

// ---------------------------------------------------------------------------
// Configurazione reparti per stampante (pos_fiscal_printer_departments)
// ---------------------------------------------------------------------------

/** Riga di GET /api/pos/fiscal-printers/:id/departments. */
export const fiscalPrinterDepartmentRowSchema = z.object({
  fiscal_department_id: z.string(),
  code: z.number().int(),
  name: z.string(),
  rt_class: RtClassKeySchema,
  rt_class_label: z.string(),
  is_class_default: z.boolean(),
  /** Reparto disattivato in anagrafica ma ancora collegato alla stampante. */
  department_is_active: z.boolean(),
  vat_code: z.string().nullable(),
  vat_rate: wireNumeric.nullable(),
  vat_nature_code: z.string().nullable(),
});
export type FiscalPrinterDepartmentRow = z.infer<typeof fiscalPrinterDepartmentRowSchema>;

/** Riga di GET /api/pos/fiscal-printers/departments (tutte le stampanti del tenant). */
export const fiscalPrinterDepartmentsByPrinterSchema = z.object({
  printer_id: z.string(),
  departments: z.array(fiscalPrinterDepartmentRowSchema),
});
export type FiscalPrinterDepartmentsByPrinter = z.infer<typeof fiscalPrinterDepartmentsByPrinterSchema>;

/** Body PUT /api/pos/fiscal-printers/:id/departments — sostituzione integrale. */
export const fiscalPrinterDepartmentsInputSchema = z.object({
  departments: z.array(z.object({
    fiscal_department_id: UUID,
    is_class_default: z.boolean(),
  }).strict()).max(200),
}).strict().superRefine((val, ctx) => {
  const seen = new Set<string>();
  val.departments.forEach((d, i) => {
    if (seen.has(d.fiscal_department_id)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['departments', i, 'fiscal_department_id'], message: 'Reparto ripetuto nella configurazione della stampante' });
    }
    seen.add(d.fiscal_department_id);
  });
});
export type FiscalPrinterDepartmentsInput = z.infer<typeof fiscalPrinterDepartmentsInputSchema>;

/** Serializzazione canonica (ordinata per codice) della configurazione reparti
 *  di una stampante: base dell'hash di sincronizzazione push. */
export function canonicalPrinterDepartmentsString(
  rows: ReadonlyArray<Pick<FiscalPrinterDepartmentRow, 'code' | 'rt_class' | 'is_class_default' | 'department_is_active'>>,
): string {
  return [...rows]
    .filter(r => r.department_is_active)
    .sort((a, b) => a.code - b.code)
    .map(r => `${r.code}:${r.rt_class}:${r.is_class_default ? 'D' : '-'}`)
    .join('|');
}

// ---------------------------------------------------------------------------
// Suggerimento informativo di copertura (NESSUN obbligo, nessun errore):
// classi RT usate dai listini attivi per cui una stampante fisica non ha un
// reparto di default. GET /api/pos/fiscal-printers/coverage-hints.
// ---------------------------------------------------------------------------
export interface FiscalDepartmentCoverageHint {
  rt_class: RtClassKey;
  label: string;
  /** Listini attivi che usano almeno una riga IVA di questa classe. */
  price_list_count: number;
  /** Stampanti (fisiche e digitali) senza reparto di default per la classe. */
  printers_without_default: Array<{ printer_id: string; printer_name: string }>;
}
